/**
 * teamMeetingPrint — הדפסה / שמירה כ-PDF לישיבת צוות
 */

import {
  ACTION_STATUS_LABELS,
  formatMeetingDateTime,
  MEETING_ROLE_LABELS,
  MEETING_STATUS_LABELS,
} from "./constants";

const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

function buildPrintStyles(): string {
  return `
    @page { margin: 14mm 12mm; size: A4 portrait; }
    * { box-sizing: border-box; }
    body {
      font-family: "David", "Arial Hebrew", Arial, sans-serif;
      direction: rtl;
      color: #111;
      margin: 0;
      padding: 0;
      font-size: 13px;
      line-height: 1.45;
    }
    h1 { font-size: 20px; margin: 0 0 4px; }
    h2 { font-size: 15px; margin: 18px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
    h3 { font-size: 14px; margin: 12px 0 4px; }
    .meta { color: #555; margin: 0 0 12px; font-size: 12px; }
    ol, ul { padding-right: 22px; margin: 0 0 8px; }
    li { margin-bottom: 8px; }
    .notes { color: #444; font-size: 12px; }
    .block { margin-bottom: 10px; }
    .badge { font-size: 11px; color: #555; }
  `;
}

type PrintMeeting = {
  title?: string | null;
  scheduledAt?: string | null;
  status?: string | null;
  durationMinutes?: number | null;
  completedAt?: string | null;
  createdByName?: string | null;
  summarySentAt?: string | null;
  participants?: Array<{
    displayName?: string | null;
    guestName?: string | null;
    meetingRole?: string;
    attended?: boolean;
  }>;
  agendaItems?: Array<{
    title?: string;
    notes?: string | null;
    submittedByName?: string | null;
    wasDiscussed?: boolean;
  }>;
  topics?: Array<{
    id?: string;
    title?: string;
    summary?: string | null;
    decisions?: string | null;
  }>;
  actionItems?: Array<{
    id?: string;
    topicId?: string | null;
    description?: string;
    technicalDetails?: string | null;
    assignedToName?: string | null;
    dueDate?: string | null;
    status?: string;
  }>;
};

export function buildTeamMeetingPrintHtml(meeting: PrintMeeting | null | undefined): string {
  if (!meeting) return "";
  const when = formatMeetingDateTime(meeting.scheduledAt) || "—";
  const status = MEETING_STATUS_LABELS[meeting.status || ""] || meeting.status || "";
  const metaBits = [
    when,
    status,
    meeting.durationMinutes != null ? `משך: ${meeting.durationMinutes} דק׳` : null,
    meeting.completedAt ? `הושלמה: ${formatMeetingDateTime(meeting.completedAt)}` : null,
    meeting.createdByName ? `נוצר ע״י: ${meeting.createdByName}` : null,
    meeting.summarySentAt
      ? `סיכום נשלח: ${formatMeetingDateTime(meeting.summarySentAt)}`
      : null,
  ].filter(Boolean);
  const participants = meeting.participants || [];
  const participantsHtml = participants.length
    ? `<ul>${participants
        .map((p) => {
          const name = escapeHtml(p.displayName || p.guestName || "");
          const role = MEETING_ROLE_LABELS[p.meetingRole || ""] || "";
          const attend = p.attended ? "נכח" : "לא סומן";
          return `<li>${name}${role ? ` — ${escapeHtml(role)}` : ""} <span class="badge">(${attend})</span></li>`;
        })
        .join("")}</ul>`
    : '<p class="notes">אין משתתפים</p>';
  const agenda = meeting.agendaItems || [];
  const agendaHtml = agenda.length
    ? `<ol>${agenda
        .map(
          (a) => `
        <li>
          <strong>${escapeHtml(a.title)}</strong>
          <span class="badge"> — ${a.wasDiscussed ? "נידון" : "לא נידון"}</span>
          ${a.submittedByName ? `<div class="notes">הוגש ע״י ${escapeHtml(a.submittedByName)}</div>` : ""}
          ${a.notes ? `<div class="notes">${escapeHtml(a.notes)}</div>` : ""}
        </li>`,
        )
        .join("")}</ol>`
    : '<p class="notes">אין נושאים בסדר היום</p>';
  const topics = meeting.topics || [];
  const topicById: Record<string, (typeof topics)[number]> = {};
  topics.forEach((t) => {
    if (typeof t.id === "string" && t.id) topicById[t.id] = t;
  });
  const topicsHtml = topics.length
    ? topics
        .map(
          (t) => `
        <div class="block">
          <h3>${escapeHtml(t.title)}</h3>
          <p><strong>סיכום:</strong> ${escapeHtml(t.summary?.trim() ? t.summary : "—")}</p>
          <p><strong>החלטות:</strong> ${escapeHtml(t.decisions?.trim() ? t.decisions : "—")}</p>
        </div>`,
        )
        .join("")
    : '<p class="notes">אין סיכומים</p>';
  const actions = meeting.actionItems || [];
  const actionsHtml = actions.length
    ? `<ul>${actions
        .map((a) => {
          const st = ACTION_STATUS_LABELS[a.status || ""] || a.status || "";
          const topicTitle = a.topicId ? topicById[a.topicId]?.title : null;
          return `<li>
        <strong>${escapeHtml(a.description)}</strong>
        ${st ? ` <span class="badge">[${escapeHtml(st)}]</span>` : ""}
        ${a.assignedToName ? ` — ${escapeHtml(a.assignedToName)}` : ""}
        ${a.dueDate ? ` (יעד: ${escapeHtml(toDateLabel(a.dueDate))})` : ""}
        ${topicTitle ? `<div class="notes">נושא: ${escapeHtml(topicTitle)}</div>` : ""}
        ${a.technicalDetails?.trim() ? `<div class="notes">${escapeHtml(a.technicalDetails)}</div>` : ""}
      </li>`;
        })
        .join("")}</ul>`
    : '<p class="notes">אין דרכי פעולה</p>';
  return `
    <h1>${escapeHtml(meeting.title || "ישיבת צוות")}</h1>
    <p class="meta">${escapeHtml(metaBits.join(" · "))}</p>
    <h2>משתתפים</h2>
    ${participantsHtml}
    <h2>סדר יום</h2>
    ${agendaHtml}
    <h2>סיכומים והחלטות</h2>
    ${topicsHtml}
    <h2>דרכי פעולה</h2>
    ${actionsHtml}
  `;
}

function toDateLabel(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("he-IL");
}

export function printTeamMeeting(
  meeting: PrintMeeting | null | undefined,
  title?: string,
): void {
  const docTitle = title || meeting?.title || "ישיבת צוות";
  const bodyHtml = buildTeamMeetingPrintHtml(meeting);
  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (!printWindow) {
    window.alert("לא ניתן לפתוח חלון הדפסה — בדוק חוסם pop-up");
    return;
  }
  printWindow.document.write(`<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(docTitle)}</title>
  <style>${buildPrintStyles()}</style>
</head>
<body>${bodyHtml}</body>
</html>`);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 400);
}
